/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GUILD_MESSAGE, NOTIFICATION_MESSAGE } from 'i18n/constants'
import { notification } from 'antd'

import ModalManagerMember from 'features/Guild/components/ModalManagerMember'
import { RequestLoginModal, RaceTable } from 'features/Race/components'
import { useAppSelector, useDebounce, useToggle, useUpdateEffect } from 'hooks'
import { Career, GetRaceListParams, RewardSortType, TypeSorts } from 'models'
import { SearchInput } from 'shared'
import { STRING, NUMBER } from 'apps/constants'
import { guildRewardSetting } from 'utils/columns'
import constants from 'apps/constants'
import guildApi from 'apis/guildApi'
import { handleAsyncRequest, shortenUserName } from 'utils/helper'
import VaultSettingStyled, { ButtonSyled } from './styled'
import Button from 'shared/Button'
import ModalUserCreateRewardSetting from './components/ModalUserCreateRewardSetting'
import SelectCustom from 'shared/SelectCustom'
import ModalConfirm from 'features/Guild/components/ModalConfirmEditGuild'

const defaultParams: GetRaceListParams = {
  limit: NUMBER.THIRTY,
  page: NUMBER.ONE,
  active: '1',
}

const defaultTypeSorts: TypeSorts = [
  {
    name: RewardSortType.Ascending,
    isActive: false
  },
  {
    name: RewardSortType.Decrease,
    isActive: false
  },
]

export default function VaultSetting() {
  const { t } = useTranslation()
  const [api, contextHolder] = notification.useNotification()
  const { profile } = useAppSelector(state => state)
  const [params, setParams] = useState<GetRaceListParams>(defaultParams)
  const [searchValue, setSearchValue] = useState<string>('')
  const debounceSearchValue = useDebounce<string>(searchValue, constants.DEBOUNCE_TIME)
  const [isRequestLoginModalOpen, toggleIsRequestLoginModalOpen] = useToggle(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [rewardSetting, setRewardSetting] = useState<any>([])
  const [isLoaded, setIsLoaded] = useState<any>(true)
  const [total, setTotal] = useState()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const userIdRef = useRef<any>(null)
  const [isModalGuildRaceCreate, setIsModalGuildRaceCreate] = useToggle(false)
  const [classSelect, setClassSelect] = useState<any>('Sorted by')
  const selectRef = useRef<any>(null)
  const [isConfirmDelete, setIsConfirmDelete] = useState(false)
  const [informationDelete, setInformationDelete] = useState<any>(undefined)
  const [loadingSubmit, setLoadingSubmit] = useState(false)

  const handleClassSortClicked = (item: string) => {
    const handleKeySort = () => {
      switch (item) {
        case RewardSortType.Ascending:
          return 'package_code-asc'
        case RewardSortType.Decrease:
        default:
          return 'package_code-desc'
      }
    }

    selectRef.current = handleKeySort()
    setParams({
      ...params,
      page: 1,
      sort: selectRef.current,
      search: debounceSearchValue,
    })
    setClassSelect(item)
  }

  const memoizedResultListSelectionColumns = useMemo(() => {
    return guildRewardSetting
  }, [profile])

  useEffect(() => {
    if (profile.guild_id !== NUMBER.ZERO) {
      getListGuildRewardSetting()
    }
  }, [params, profile])

  const getListGuildRewardSetting = async () => {
    setLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.getGuildRewardSetting(profile.guild_id, { ...params }))

    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[0]?.message)
    }

    if (result) {
      const formatData = result.data.records.map((record: any) => {
        return {
          ...record,
          member: shortenUserName(record.user_name),
          winRate: handleCareer(record.career),
          raceName: record.name
        }
      })
      setRewardSetting(params.page > NUMBER.ONE ? [...rewardSetting, ...formatData] : formatData)
      setTotal(result.data?.total || NUMBER.ZERO)
      setIsLoaded(false)
    }

    setLoading(false)
  }

  useUpdateEffect(() => {
    setParams({ ...params, page: NUMBER.ONE, search: debounceSearchValue.trim() })
  }, [debounceSearchValue])

  const handleSearchValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const openNotification = (message: string, description: string) => {
    notification.config({
      duration: NUMBER.FOUR
    })

    api.info({
      message,
      description,
      placement: 'bottomRight',
      className: message === STRING.ERROR ? 'ant-custom-error' : 'ant-custom-success'
    })
  }

  const handleCareer = (career: Career) => {
    const totalRace = career?.total_number_of_races
    const firstClass = career?.first_count
    const secondClass = career?.second_count
    const thirdClass = career?.third_count
    const tltClass = (firstClass + secondClass + thirdClass) / totalRace
    const newTltClass = isNaN(tltClass) ? 0 : tltClass * 100
    const winRateResult = `${
      Number.isInteger((firstClass / totalRace) * 100)
        ? (firstClass / totalRace) * 100
        : ((firstClass / totalRace) * 100).toFixed(2)
    }% - ${Number.isInteger(newTltClass) ? newTltClass : newTltClass.toFixed(2)}%`
    let valueDisplay = ''
    if (totalRace === 0) {
      valueDisplay = '---'
    }
    if (totalRace > 0 && firstClass === 0 && secondClass === 0 && thirdClass === 0) {
      valueDisplay = '0.00% - 0.00%'
    }
    if (totalRace !== 0 && (firstClass !== 0 || secondClass !== 0 || thirdClass !== 0)) {
      valueDisplay = winRateResult
    }
    return valueDisplay
  }

  const _handleToggleModal = () => {
    setShowModal(!showModal)
  }

  const _handleUpgrade = async () => {
    setIsLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.putAppove(profile.guild_id, userIdRef.current))
    setIsLoading(false)
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[NUMBER.ZERO]?.message)
    }

    if (result) {
      getListGuildRewardSetting()
      _handleToggleModal()
      openNotification(STRING.SUCCESS, t(`${GUILD_MESSAGE}.message.approve_success`))
    }
  }

  const handleConfirmClick = () => {
    setParams({ ...params, page: NUMBER.ONE, search: debounceSearchValue })
  }

  const handleCloseGuildRaceCreate = () => {
    setIsModalGuildRaceCreate(!isModalGuildRaceCreate)
    setInformationDelete(undefined)
  }

  const handleEdit = async (row: any) => {
    const [error, result]: any = await handleAsyncRequest(guildApi.getGuildRewardSettingDetail(row?.id))
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[0]?.message)
    }
     if (result) {
      setInformationDelete(result.data)
      setIsModalGuildRaceCreate(true)
    }
  }

  const handleDelete = (row: any) => {
    setIsConfirmDelete(true)
    setInformationDelete(row)
  }

  const handleConfirmDelete = async () => {
    setLoadingSubmit(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.postGuildDeleteTemplate(informationDelete?.id))
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[0]?.message)
    }
    if (result) {
      setIsConfirmDelete(false)
      setInformationDelete(undefined)
      notification.success({
        message: 'SUCCESS',
        description: 'Delete Reward Template successfully!',
        placement: 'bottomRight',
        duration: 4,
        className: 'ant-custom-success'
      })
      setParams({
        ...params,
        page: 1,
      })
    }
    setLoadingSubmit(false)
  }

  return (
    <VaultSettingStyled>
      {contextHolder}
      <div className='search-box'>
        {!isLoaded && profile?.role_in_guild === 'MASTER' && rewardSetting?.length < 24 && (
          <ButtonSyled>
            <Button
              buttonName='CREATE TEMPLATE'
              onClickButton={handleCloseGuildRaceCreate}
              width={150}
              height={32}
              isLoading={false}
              margingTop={true}
              classNames='fs-18'
            />
          </ButtonSyled>
        )}
        <SelectCustom
          dataSelect={defaultTypeSorts}
          nameSelect={classSelect}
          onSelected={handleClassSortClicked}
          customClass='select-custom'
          width={200}
        />
        <SearchInput
          searchValue={searchValue}
          handleSearchValueChanged={handleSearchValueChanged}
          customClass='search-horse-input'
          placeholder={t(`${GUILD_MESSAGE}.searchRewardCode`)}
        />
      </div>
      <div className='members'>
        <RaceTable
          columns={memoizedResultListSelectionColumns}
          data={rewardSetting ?? []}
          raisePage={setParams}
          loader={!loading}
          params={params}
          checkLoadingPage={false}
          totalRace={total}
          currentUser={profile}
          noDataText={t(`${NOTIFICATION_MESSAGE}.dataNotFound`)}
          isShowVipIcon={true}
          handleClickedEditRow={handleEdit}
          handleClickedDeleteRow={handleDelete}
          isEnableAction={profile?.role_in_guild === 'MASTER'}
        />
      </div>

      {showModal && (
        <ModalManagerMember
          title={t(`${GUILD_MESSAGE}.title_modal_approve`)}
          message={
            <p>
              {t(`${GUILD_MESSAGE}.content_modal_approve`)}
              <br />
              {t(`${GUILD_MESSAGE}.content_modal_approve2`)}
            </p>
          }
          onClose={_handleToggleModal}
          onConfirm={_handleUpgrade}
          loading={isLoading}
          titleColor='rgb(78, 240, 118)'
          width={515}
          heigth={240}
        />
      )}

      {isRequestLoginModalOpen && <RequestLoginModal toggleIsModalOpen={toggleIsRequestLoginModalOpen} />}

      {isModalGuildRaceCreate && (
        <ModalUserCreateRewardSetting
          onConfirmCreateRaceClick={handleConfirmClick}
          toggleIsModalOpen={setIsModalGuildRaceCreate}
          onCloseButtonClick={handleCloseGuildRaceCreate}
          inforEdit={informationDelete}
        />
      )}

      {isConfirmDelete && (
        <ModalConfirm
          onClose={() => setIsConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
          titleModal='Confirm'
          loading={loadingSubmit}
          content={
            <div>
              Are you sure you want to delete this reward template?
            </div>
          }
          margingTop={true}
          widthM={550}
          heightM={235}
        />
      )}
    </VaultSettingStyled>
  )
}
